import { useEffect, useState } from "react";

import { FaChevronDown, FaChevronUp, FaGooglePlay } from "react-icons/fa";
import { paletteFor } from "../data/site";
import projectMarks from "../components/ui/projectMarks";

function PolicyText({ parts }) {
  if (typeof parts === "string") return parts;
  return parts.map((part) =>
    part.href ? (
      <a key={part.text} href={part.href}>
        {part.text}
      </a>
    ) : (
      <span key={part.text}>{part.text}</span>
    )
  );
}

function contactHref(contact) {
  if (!contact) return null;
  return contact.startsWith("http") ? contact : `mailto:${contact}`;
}

function isAdsSwitch(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value) && "content" in value && "ads" in value;
}

function shown(list, ads) {
  if (!list) return [];
  return list
    .filter((item) => !isAdsSwitch(item) || Boolean(item.ads) === Boolean(ads))
    .map((item) => (isAdsSwitch(item) ? item.content : item));
}

function pieceKey(parts) {
  if (typeof parts === "string") return parts;
  if (Array.isArray(parts)) return parts.map((part) => part.text).join("");
  return "block";
}

function PrivacyDocument({ privacy, ads = false }) {
  const href = contactHref(privacy.contact);
  const intro = shown(privacy.intro, ads);
  let sectionNumber = 0;
  return (
    <>
      <p className="product-policy-name">{privacy.name}</p>
      {privacy.app ? (
        <p className="product-updated">
          {privacy.appLabel || "App"}: {privacy.app}
        </p>
      ) : null}
      {privacy.developer ? (
        <p className="product-updated">
          {privacy.developerLabel || "Developer"}: {privacy.developer}
        </p>
      ) : null}
      <p className="product-updated">
        {privacy.dateLabel || "Effective date"}: {privacy.updated}
      </p>
      {privacy.contact ? (
        <p className="product-updated">
          {privacy.contactLabel || "Contact"}: <a href={href}>{privacy.contact}</a>
        </p>
      ) : null}
      {privacy.package ? <p className="product-updated">App package: {privacy.package}</p> : null}
      {intro.map((parts) => (
        <p key={pieceKey(parts)}>
          <PolicyText parts={parts} />
        </p>
      ))}
      {privacy.sections.map((section) => {
        if (section.ads === true && !ads) return null;
        const paragraphs = shown(section.paragraphs, ads);
        const items = shown(section.items, ads);
        const notes = shown(section.notes, ads);
        if (!paragraphs.length && !items.length && !notes.length && !section.table) return null;
        const numbered = /^\d+\.\s/.test(section.heading);
        const heading = numbered
          ? `${(sectionNumber += 1)}. ${section.heading.replace(/^\d+\.\s*/, "")}`
          : section.heading;
        return (
        <section key={section.heading}>
          <h2>{heading}</h2>
          {paragraphs.map((parts) => (
            <p key={pieceKey(parts)}>
              <PolicyText parts={parts} />
            </p>
          ))}
          {items.length ? (
            <ul>
              {items.map((item) => (
                <li key={pieceKey(item)}>
                  <PolicyText parts={item} />
                </li>
              ))}
            </ul>
          ) : null}
          {section.table ? (
            <table>
              <thead>
                <tr>
                  {section.table.headers.map((header) => (
                    <th key={header}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {section.table.rows.map((row) => (
                  <tr key={row[0]}>
                    {row.map((cell) => (
                      <td key={cell}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {notes.map((parts) => (
            <p key={pieceKey(parts)}>
              <PolicyText parts={parts} />
            </p>
          ))}
        </section>
        );
      })}
    </>
  );
}

function PolicyPanel({ title, children, id }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!id) return undefined;
    const sync = () => {
      if (window.location.hash === `#${id}`) setOpen(true);
    };
    sync();
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, [id]);

  return (
    <section className="product-privacy" id={id}>
      <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <span>{title}</span>
        {open ? <FaChevronUp aria-hidden="true" /> : <FaChevronDown aria-hidden="true" />}
      </button>
      {open ? <div className="product-privacy-body">{children}</div> : null}
    </section>
  );
}

export default function ProductPage({ project }) {
  const palette = paletteFor(project);
  const accent3 = palette.accent3 || palette.accent2;
  const icons = (project.marks || []).map((name) => projectMarks[name]).filter(Boolean);

  return (
    <div
      className="product-page"
      style={{
        "--product-bg": palette.bg,
        "--product-ink": palette.ink,
        "--product-muted": palette.muted,
        "--product-accent": palette.accent,
        "--product-accent-2": palette.accent2,
        "--product-accent-3": accent3,
        "--product-card": palette.card,
      }}
    >
      <a href="/" className="product-mark">
        IM
      </a>

      <div className="product-hero">
        <div className="product-copy">
          <p className="product-kicker">In development</p>
          <h1>
            <span>{project.title}</span>
            {icons.length ? (
              <span className="product-icons">
                {icons.map((Icon, index) => (
                  <Icon key={project.marks[index]} aria-hidden="true" size={32} />
                ))}
              </span>
            ) : null}
          </h1>
          <p className="product-lede">{project.description}</p>
          {project.store ? (
            <a className="product-store" href={project.store} target="_blank" rel="noopener noreferrer">
              <FaGooglePlay aria-hidden="true" />
              Download on Play Store
              <span className="sr-only"> (opens in a new tab)</span>
            </a>
          ) : (
            <span className="product-store">
              <FaGooglePlay aria-hidden="true" />
              Download on Play Store
            </span>
          )}
        </div>
        <ul className="product-features">
          {project.features.map((feature) => (
            <li key={feature.title}>
              <h2>{feature.title}</h2>
              <p>{feature.text}</p>
            </li>
          ))}
        </ul>
      </div>

      <PolicyPanel title="Privacy Policy" id="privacy">
        {project.privacy.sections ? (
          <PrivacyDocument privacy={project.privacy} ads={project.ads} />
        ) : (
          <>
            <p className="product-updated">Last updated: {project.privacy.updated}</p>
            <p>
              This is a draft for {project.title}. The app is still in development. Update this policy before release.
            </p>
            <h2>Information we may collect</h2>
            <ul>
              {project.privacy.collects.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <h2>How we may use it</h2>
            <ul>
              {project.privacy.uses.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <h2>Delete your information</h2>
            <p>
              Account deletion steps will be added if the app has accounts. Until then, email the address below and ask for your information to be deleted.
            </p>
            <h2>Data security</h2>
            <p>
              The app will use ordinary care to protect information it stores. No method of storage or transmission is completely secure.
            </p>
            <h2>Children&apos;s privacy</h2>
            <p>
              This app is not directed at anyone under 13. If a child has sent personal information, contact the address below.
            </p>
            <h2>Your rights</h2>
            <p>You can ask to see, correct, or delete personal information the app holds about you.</p>
            <h2>Contact</h2>
            <p>
              <a href="mailto:imran.melikov84@gmail.com">imran.melikov84@gmail.com</a>
            </p>
          </>
        )}
      </PolicyPanel>
      {project.terms?.sections ? (
        <PolicyPanel title="Terms of Use" id="terms">
          <PrivacyDocument privacy={project.terms} ads={project.ads} />
        </PolicyPanel>
      ) : null}
    </div>
  );
}
